import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from '@/components/ui/drawer'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useCurrentPubkey } from '@/atoms/session'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading03Icon } from '@hugeicons/core-free-icons'
import { NostrEvent } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { now } from '@/lib/utils'
import { getReplaceableCoordinateFromEvent, isReplaceableEvent } from '@/lib/event'
import client from '@/services/client.service'
import { useAtomValue } from 'jotai'

export default function ReportDialog({
  event,
  isOpen,
  closeDialog
}: {
  event: NostrEvent
  isOpen: boolean
  closeDialog: () => void
}) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)

  if (isSmallScreen) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog()
          }
        }}
      >
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="hidden" />
            <DrawerDescription className="hidden" />
          </DrawerHeader>
          <div className="p-4">
            <ReportContent event={event} closeDialog={closeDialog} />
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeDialog()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="hidden" />
          <DialogDescription className="hidden" />
        </DialogHeader>
        <ReportContent event={event} closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function ReportContent({ event, closeDialog }: { event: NostrEvent; closeDialog: () => void }) {
  const { t } = useTranslation()
  const pubkey = useCurrentPubkey()
  const [reason, setReason] = useState<string | null>(null)
  const [reporting, setReporting] = useState(false)

  const handleReport = async () => {
    if (!reason || !pubkey) return

    try {
      setReporting(true)

      const tags: string[][] = []
      if (event.kind === kinds.Metadata) {
        tags.push(['p', event.pubkey, reason])
      } else {
        tags.push(['p', event.pubkey])
        tags.push(['e', event.id, reason])
        if (isReplaceableEvent(event.kind)) {
          tags.push(['a', getReplaceableCoordinateFromEvent(event), reason])
        }
      }

      await client.publish({
        kind: kinds.Report,
        content: '',
        tags,
        created_at: now()
      })

      toast.success(t('Successfully report'))
      closeDialog()
    } catch (error) {
      console.warn('failed to report:', (error as Error).message)
      toast.error(t('Failed to report: {{error}}', { error: (error as Error).message }))
    } finally {
      setReporting(false)
    }
  }

  return (
    <div className="w-full space-y-4">
      <RadioGroup value={reason} onValueChange={setReason} className="space-y-2">
        {['nudity', 'malware', 'profanity', 'illegal', 'spam', 'other'].map((item) => (
          <div key={item} className="flex items-center space-x-2">
            <RadioGroupItem value={item} id={item} />
            <Label htmlFor={item} className="text-base">
              {t(item)}
            </Label>
          </div>
        ))}
      </RadioGroup>
      <Button
        variant="destructive"
        className="w-full"
        disabled={!reason || reporting}
        onClick={(e) => {
          e.stopPropagation()
          handleReport()
        }}
      >
        {reporting && <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />}
        {t('Report')}
      </Button>
    </div>
  )
}
